import { useCallback } from 'react';

import useEnv from '@zenbusiness/application-commons-env';
import { useCaptureException } from '@zenbusiness/application-commons-logging';

import { startMyAccountOperation } from '#client/utils/myAccount';

/**
 * Starts a My Account API operation, which leaves the page for Auth0's MFA challenge and comes
 * back on the security callback.
 *
 * @returns {(operation: string, options?: { returnTo?: string, replaceMethodIds?: string[] }) => Promise<boolean>}
 *   Resolves false when the operation couldn't start, so the caller can tell the customer.
 */
export default function useMyAccountOperation() {
  const { AUTH0_DOMAIN: domain, OIDC_CLIENT_ID: clientId } = useEnv('root-first');
  const captureException = useCaptureException();

  return useCallback(
    async (operation, { returnTo = window.location.pathname, replaceMethodIds } = {}) => {
      try {
        await startMyAccountOperation({ domain, clientId, operation, returnTo, replaceMethodIds });
        return true;
      } catch (error) {
        // Usually blocked storage, so the PKCE verifier can't survive the redirect; the customer stays put.
        captureException(error);
        return false;
      }
    },
    [domain, clientId, captureException],
  );
}
